import axios from 'axios';
import { VIDEO_URL } from 'util/constants';
import { logger } from 'services/Logger';
import { getAuth, extractRenderData } from '../../util';

export class FreakVideoService {
  async render(cloudVideoFileName: string, overlayData: any): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required for video service');
    const token = await user.getIdToken();

    await axios.post(
      `${VIDEO_URL}/render`,
      {
        filename: cloudVideoFileName,
        values: overlayData,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  }

  async convertTo1080p30fps(cloudVideoFileName: string): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Authentication required for video service');
    const token = await user.getIdToken();

    const response = await axios.post(
      `${VIDEO_URL}/convert1080p30fps`,
      cloudVideoFileName,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (response.data && response.data.success && response.data.filename) {
      return response.data.filename;
    }
    throw new Error('Video conversion failed');
  }
}

export const freakVideoService = new FreakVideoService();

// Typdefinition für OverlayValue (analog Backend, figures als Array)
export interface OverlayFigure {
  name: string;
  score?: number;
  achieved?: boolean;
}

export interface OverlayValue {
  time: number; // Sekunden
  jump_distance: number; // cm
  distance: number; // cm
  jump_height: number; // cm
  height_difference_up: number; // cm
  airtime: number; // s
  speed: number; // km/h
  figure?: string | OverlayFigure;
}

export function buildOverlayDataFromPlay(
  play: any,
  fps: number = 30,
  interval: number = 0.1
): any {
  logger.debug('[buildOverlayDataFromPlay] called', { fps, interval, play });
  if (!play || !Array.isArray(play.steps) || play.steps.length === 0) {
    logger.warn('[buildOverlayDataFromPlay] No valid play or steps array');
    // Fallback: Dummy-Wert, damit das Backend nicht crasht
    return {
      render_type: 'TM',
      score_play: play?.score_play ?? 0,
      values: [{
        time: 0,
        jump_distance: 0,
        distance: 0,
        jump_height: 0,
        height_difference_up: 0,
        airtime: 0,
        speed: 0,
        figure: '',
      }]
    };
  }
  // RenderData extrahieren (wie RunStatsBox)
  const renderData = extractRenderData(play);
  // Für jeden Step im Play einen Overlay-Datenpunkt erzeugen
  const overlay: OverlayValue[] = play.steps.map((step: any, stepIdx: number) => {
    const t = step.t_ms / 1000;
    const ms = step.t_ms;
    function getVal<K extends keyof typeof renderData>(key: K): number {
      const arr = renderData[key];
      if (!arr || arr.length === 0) return 0;
      let idx = arr.findIndex((d: any) => d.start <= ms && d.end >= ms);
      if (idx === -1) idx = arr.length - 1;
      const d = arr[idx];
      if (!d) return 0;
      if (typeof d.value === 'number') {
        if (d.end > d.start && typeof d.lastValue === 'number') {
          const frac = (ms - d.start) / (d.end - d.start);
          return Number(d.lastValue) + frac * (Number(d.value) - Number(d.lastValue));
        }
        return d.value;
      }
      return 0;
    }
    // Figure-Objekt bestimmen (indexbasiert!)
    let figure: string | OverlayFigure = '';
    if (Array.isArray(play.figures) && play.figures.length > 0) {
      const currentFigure = play.figures.find(
        (fig: any) => {
          const startIdx = fig.i_beg ?? 0;
          const endIdx = fig.i_end ?? 0;
          return stepIdx >= startIdx && stepIdx < endIdx;
        }
      );
      if (currentFigure) {
        figure = {
          name: currentFigure.name ?? '',
          score: currentFigure.score ?? undefined,
          achieved: currentFigure.achieved ?? undefined,
        };
      } else {
        figure = '';
      }
    }
    return {
      time: Number(t.toFixed(2)),
      jump_distance: Number(getVal('fly_width_m') * 100).toFixed(2),
      distance: Number(getVal('travel_distance_m') * 100).toFixed(2),
      jump_height: Number(getVal('fly_height_m') * 100).toFixed(2),
      height_difference_up: Number(getVal('height_difference_m') * 100).toFixed(2),
      airtime: Number(getVal('air_time_s')).toFixed(2),
      speed: Number(getVal('speed')).toFixed(2),
      figure,
    };
  });
  logger.debug('[buildOverlayDataFromPlay] overlay array created', { overlay });
  // render_type anhand displaySetting setzen und score aus play holen
  const displaySetting = (window as any).appData?.displaySettings?.defaultViewMode;
  const render_type = displaySetting === 'score' ? 'SC' : 'TM';
  return {
    render_type,
    score_play: play?.score ?? 0,
    values: overlay,
  };
}
